import React, { useCallback } from 'react';

import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Flex, Popconfirm, Typography } from 'antd';

import { useDeleteErrorNotif } from '../api/deleteErrorNotif';
import { IErrorNotif } from '../types';
import { scopes } from '@/config';
import useAppState from '@/hooks/useAppState';
import useToasts from '@/shared/hooks/useToasts';
import { MessageType } from '@/shared/hooks/useToasts.types';

interface IErrorNotifItemProps {
  notif: IErrorNotif;
  partnerId: string;
  onEdit: (notif: IErrorNotif) => void;
}

export const ErrorNotifItem = ({
  notif,
  partnerId,
  onEdit,
}: IErrorNotifItemProps) => {
  const addToast = useToasts((state) => state.addItem);
  const userScopes = useAppState((state) => state.scopes);
  const canManage = userScopes.includes(scopes.MANAGE_WEBHOOKS);
  const { mutateAsync: deleteNotif, isPending } = useDeleteErrorNotif({
    partnerId,
  });

  const handleDelete = useCallback(async () => {
    try {
      await deleteNotif(notif.notif_id);
      addToast({ msg: 'Email removed', type: MessageType.Success }, 'root', {});
    } catch (err) {
      addToast(
        {
          msg: err instanceof Error ? err.message : 'Delete failed',
          type: MessageType.Error,
        },
        'root',
        {}
      );
    }
  }, [deleteNotif, notif.notif_id, addToast]);

  return (
    <Flex
      justify="space-between"
      align="center"
      style={{
        padding: '10px 16px',
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        marginBottom: 8,
      }}
    >
      <Typography.Text>{notif.email}</Typography.Text>
      {canManage && (
        <Flex gap={4}>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => onEdit(notif)}
            data-testid={`error-notif-edit-${notif.notif_id}-button`}
          />
          <Popconfirm
            title="Remove notification email?"
            description="This action cannot be undone."
            okText="Remove"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={handleDelete}
            disabled={isPending}
            rootClassName="webhook-popconfirm"
          >
            <Button
              type="text"
              icon={<DeleteOutlined />}
              danger
              loading={isPending}
              data-testid={`error-notif-delete-${notif.notif_id}-button`}
            />
          </Popconfirm>
        </Flex>
      )}
    </Flex>
  );
};
